import DropDown from "./Inputs/DropDown";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import Boolean from "./Inputs/Boolean";
import TextField from "./Inputs/TextField";
import Error from "../../Alerts/Error";
import Success from "../../Alerts/Success";
import {authContext} from "../../../context/auth";
import {useParams} from "react-router-dom";
import File from "./Inputs/File";
import { useUploadQaHolds } from "../../../util/uploadQa";
import filterResponse from "../../../filterResponse";
import Spinner from "../../Spinner";
import useCanContinue, {answersComplete} from "../../../memos/useCanContinue";
import List from "./Inputs/List";
import RegexField from "./Inputs/RegexField";
import {progressContext} from "../../../context/progress";
import MainInfoQaIssues from "./MainInfoQaIssues";

// The main-data QA checks run on the server once a main info save has settled (a 5 s debounce), so
// their verdict is fetched again a little after that.
const MAIN_QA_REFRESH_DELAY = 6500;

// The inputs mutate the objects inside answers in place, so a snapshot kept for Cancel has to be a
// deep copy — a shallow one would be edited along with the live state and restore nothing.
function cloneAnswers(answers) {
	return JSON.parse(JSON.stringify(answers));
}

export default function MainInfo({job, enable, disable, hasEntryInputs, goto}) {
	const [answers, setAnswers] = useState({
		booleans: [... job.generalInputs.booleans?.map(value => {
			return  {
				checked: false,
				...value
			}
		})],
		dropDowns: [...job.generalInputs.dropDowns?.map(value => {
			return {
				value: '',
				...value
			};
		})],
		dropDownsMultiple: [...job.generalInputs.dropDownsMultiple?.map(value => {
			return {
				value: [],
				...value
			};
		})],
		lists: [...job.generalInputs.lists?.map(value => {
			return {
				value: [],
				...value
			};
		})],
		textFields: [...job.generalInputs.textFields?.map(value => {
			return {
				value: '',
				...value
			};
		})],
		regexFields: [...job.generalInputs.regexFields?.map(value => {
			return {
				value: '',
				...value
			};
		})],
		files: [...job.generalInputs.files?.map(value => {
			return {
				key: '',
				...value
			};
		})]
	});

    const { user} = useContext(authContext);
	const {progress, getProgress} = useContext(progressContext);
	const qaRefresh = useRef(null);
	// the context hands out a new getProgress every render; the timer calls whichever is current
	const getProgressRef = useRef(getProgress);
	getProgressRef.current = getProgress;
	const scheduleQaRefresh = useCallback(() => {
		clearTimeout(qaRefresh.current);
		qaRefresh.current = setTimeout(() => getProgressRef.current(), MAIN_QA_REFRESH_DELAY);
	}, []);
	useEffect(() => () => clearTimeout(qaRefresh.current), []);
    const {id} = useParams();
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
	const [otherDisabledReason, setOtherDisabledReason] = useState(false);
	const [uploadQaHeld, setUploadQaHold] = useUploadQaHolds(); // a file still being checked, or one a blocker metric failed
	const [uploadsInFlight, setFileLoading] = useState(0); // count, so concurrent uploads each hold the gate
	const fileLoading = uploadsInFlight > 0;
	// Main info is answered once and then read for the rest of the job. While it was saved on every
	// keystroke, a single stray click on a dropdown — by the contributor, QA or a PM — silently
	// overwrote an answer, and with a required answer blanked the whole delivery went out of reach.
	// So once a complete set of answers exists the form is locked, and changing it is deliberate:
	// Edit, then Save.
	const [locked, setLocked] = useState(false);
	const [editing, setEditing] = useState(false);
	const [savedAnswers, setSavedAnswers] = useState(null);
	const readOnly = locked && !editing;
	// Read by the autosave effect instead of being a dependency of it: locking on Continue must not
	// re-run the effect, because its cleanup would abort the save of the change just made.
	const autosaveOff = useRef(false);
	autosaveOff.current = locked || editing;

	const inputs = [...job.generalInputs.booleans?.map(value => {
		return {
			boolean: true,
			...value
		}
	}), ...job.generalInputs.textFields?.map(value => {
		return {
			textField: true,
			...value
		}
	}), ...job.generalInputs.regexFields?.map(value => {
		return {
			regexField: true,
			...value
		}
	}), ...job.generalInputs.dropDowns?.map(value => {
		return {
			dropDown: true,
			...value
		}
	}), ...job.generalInputs.dropDownsMultiple?.map(value => {
		return {
			dropDown: true,
			multiple: true,
			...value
		}
	}), ...job.generalInputs.lists?.map(value => {
		return {
			value: [],
			list: true,
			...value
		};
	}), ...job.generalInputs.files?.map(value => {
		return {
			file: true,
			...value
		}})].sort((a, b) => a.name.localeCompare(b.name));

    useEffect(() => {
		setFetchLoading(true);

        (async () => {
            try {
                const response = await fetch(process.env.REACT_APP_ENDPOINT + `/crowdJobSubmissions?crowdJobId=${id}&freelancerId=${user.id}`, {
                    method: "GET",
					credentials: "include",
                    headers: {
                        "Content-Type": "application/json"
                    }
                });
				await filterResponse(response);

				if (response.status === 200) {
                    const {crowdJobSubmission} = await response.json();

                    if (crowdJobSubmission) {
						if (crowdJobSubmission.answers) {
							console.log('fetch answers', crowdJobSubmission.answers);
							setAnswers(crowdJobSubmission.answers);
							setSavedAnswers(cloneAnswers(crowdJobSubmission.answers));
							// A half-filled main info is still being worked on, so only a complete
							// one is protected.
							setLocked(answersComplete(crowdJobSubmission.answers));
						}
                    }
                }
				else if (response.status !== 404) {
					setLoadFailed(true);
					setError("Your main info could not be loaded. Please reload the page.");
				}
            }
            catch (e) {
                console.error(e);
				setLoadFailed(true);
                setError("Your main info could not be loaded. Please reload the page.");
            }

			setFetchLoading(false);
        })();
    }, []);

	useEffect (() => {
		const abortController = new AbortController();
		let isMounted = true;

		// Autosave only carries the first fill. Once the answers are locked, nothing reaches the
		// server until Save is pressed, so an edit that is cancelled leaves no trace.
		if (!fetchLoading && !loadFailed && !autosaveOff.current) {
			(async () => {
				setSuccess(null);
				setError(null);
				setLoading(true);

				try {
					const response = await fetch(process.env.REACT_APP_ENDPOINT + "/crowdJobSubmissions", {
						method: "POST",
						credentials: "include",
						body: JSON.stringify({
							crowdJobId: id,
							freelancerId: user.id,
							answers: answers,
						}),
						headers: {
							"Content-Type": "application/json"
						},
						signal: abortController.signal
					});
					await filterResponse(response);

					if (response.status !== 200 && response.status !== 201) {
						isMounted && setError("Failed to submit due to server error.");
					}
					else {
						isMounted && setSavedAnswers(cloneAnswers(answers));
						scheduleQaRefresh();
					}
				}
				catch (e) {
					console.error(e);
					if (e.name !== 'AbortError') {
						isMounted && setError("Failed to submit due to server error.");
					}
				}

				isMounted && setLoading(false);
			})();
		}

		return () => {
			abortController.abort();
			isMounted = false;
		}
	}, [answers, fetchLoading, loadFailed, id, user.id, scheduleQaRefresh]);

	async function saveEdits() {
		setSuccess(null);
		setError(null);
		setLoading(true);

		try {
			const response = await fetch(process.env.REACT_APP_ENDPOINT + "/crowdJobSubmissions", {
				method: "POST",
				credentials: "include",
				body: JSON.stringify({
					crowdJobId: id,
					freelancerId: user.id,
					answers: answers,
				}),
				headers: {
					"Content-Type": "application/json"
				}
			});
			await filterResponse(response);

			if (response.status === 200 || response.status === 201) {
				setSavedAnswers(cloneAnswers(answers));
				setEditing(false);
				setLocked(true);
				setSuccess("Main info saved.");
				scheduleQaRefresh();
			}
			else {
				setError("Failed to submit due to server error.");
			}
		}
		catch (e) {
			console.error(e);
			setError("Failed to submit due to server error.");
		}

		setLoading(false);
	}

	function startEditing() {
		setSuccess(null);
		setError(null);
		setSavedAnswers(cloneAnswers(answers));
		setEditing(true);
	}

	function cancelEditing() {
		setSuccess(null);
		setError(null);
		if (savedAnswers) {
			setAnswers(cloneAnswers(savedAnswers));
		}
		setEditing(false);
	}

    function onSubmit(evt) {
		evt.preventDefault();

        const errors = [];
        for (const boolean of answers.booleans) {
            if (!boolean.checked && boolean.requiredTrue) {
                errors.push(`"${boolean.name}"`);
            }
        }

        if (errors.length) {
            setError(`Please check the following checkboxes before you continue: ${errors.join(", ")}`);
            return;
        }

		if (editing) {
			saveEdits();
			return;
		}

		// The answers are complete and already saved by now, so protect them from here on.
		setLocked(true);

		if (hasEntryInputs) {
			goto(2);
		}
		else {
			goto(3);
		}
    }

	const canContinue = useCanContinue(answers);

	useEffect(() => {
		if (canContinue) {
			if (hasEntryInputs) {
				enable(2);
			}
			else {
				enable(3);
			}
		}
		else {
			if (hasEntryInputs) {
				disable(2);
			}
			else {
				disable(3);
			}
		}
	}, [canContinue, hasEntryInputs]);

	const unsavedEdits = useMemo(() => {
		return editing && savedAnswers && JSON.stringify(answers) !== JSON.stringify(savedAnswers);
	}, [editing, answers, savedAnswers]);

	// Leaving the page mid-edit would drop the changes without a word; they are only in this
	// component until Save.
	useEffect(() => {
		if (!unsavedEdits) {
			return;
		}
		const warn = (event) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [unsavedEdits]);

	const submitDisabled = useMemo(() => {
		return loadFailed || loading || fileLoading || !canContinue || otherDisabledReason || uploadQaHeld;
	}, [loadFailed, loading, canContinue, fileLoading, otherDisabledReason, uploadQaHeld]);

	const busy = loading || fileLoading;
	const busyLabel = <><Spinner small={true} /> {fileLoading ? `Uploading${uploadsInFlight > 1 ? ` ${uploadsInFlight} files` : ''}…` : ''}</>;

	return (
		<div className={"input-sections"} translate="no">
            {error && <Error message={error} />}
            {success && <Success message={success} />}
            <MainInfoQaIssues errors={progress.mainQaErrors} blocked={progress.mainQaBlocked} />
			<h4>Main info</h4>

			{readOnly && <p className={"text-muted small mb-3"}>Main info is saved. Click Edit if you need to change it.</p>}

			<form onSubmit={onSubmit} id={"mainInfoForm"} />

			{/* A disabled fieldset disables every control inside it, so read-only mode needs no
			    change in the individual inputs. */}
			{loadFailed && <button type={"button"} className={"btn btn-outline-secondary mb-3"} onClick={() => window.location.reload()}>Reload</button>}

			<fieldset disabled={readOnly || loadFailed} className={"border-0 p-0 m-0"}>
				{inputs.map(value => {
					if (value.dropDown) {
						return <DropDown formId={"mainInfoForm"} dropDown={value} answers={answers} setAnswers={setAnswers} key={value.id}/>;
					}
					if (value.boolean) {
						return <Boolean formId={"mainInfoForm"} boolean={value} answers={answers} setAnswers={setAnswers} key={value.id}/>
					}
					if (value.textField) {
						return <TextField formId={"mainInfoForm"} textField={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
					}
					if (value.regexField) {
						return <RegexField formId={"mainInfoForm"} regexField={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
					}
					if (value.list) {
						return <List formId={"mainInfoForm"} list={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
					}
					if (value.file) {
						return <File formId={"mainInfoForm"} setOtherDisabledReason={setOtherDisabledReason} setUploadQaHold={setUploadQaHold} setFileLoading={setFileLoading} file={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
					}
					return null;
				})}
			</fieldset>

			{readOnly && <div className={"d-flex gap-2 mt-3"}>
				<button type={"button"} className={"btn btn-outline-secondary"} onClick={startEditing}>Edit</button>
				<button type={"submit"} form={"mainInfoForm"} className={"btn btn-success flex-fill"} disabled={busy}>{busy ? busyLabel : 'Continue'}</button>
			</div>}

			{editing && <div className={"d-flex gap-2 mt-3"}>
				<button type={"button"} className={"btn btn-outline-secondary"} onClick={cancelEditing} disabled={busy}>Cancel</button>
				<button type={"submit"} form={"mainInfoForm"} className={"btn btn-success flex-fill"} disabled={submitDisabled}>{busy ? busyLabel : 'Save'}</button>
			</div>}

			{!locked && !editing && <button type={"submit"} form={"mainInfoForm"} className={"btn btn-success w-100 mt-3"} disabled={submitDisabled}>{busy ? busyLabel : 'Save'} </button>}
		</div>
	)
}
